import React, { useMemo } from "react";
import { View, Text, StyleSheet, Platform, Dimensions } from "react-native";
import { useSelector } from "react-redux";
import LHOverlay from "../../components/LHOverlay";
import ExpandableImage from "../../components/ExpandableImage";
import LhCardDisplay from "../../components/LhCardDisplay";
import { useTheme } from "../../theme";
import { ThemeColors } from "../../theme/types";
import {
  ObservationRowData,
  ObservationDetailData,
} from "../../types/observation";

interface ObservationDetailsProps {
  selectedRowData: ObservationRowData | null;
  isOverlayVisible: boolean;
  closeOverlay: () => void;
  isDetailLoading: boolean;
  detailApiError: string | null;
  detailedObservationData: ObservationDetailData | null;
  observationNotes?: string; // Add observationNotes as a prop outside selectedObservation
}

const ObservationDetails: React.FC<ObservationDetailsProps> = ({
  selectedRowData,
  isOverlayVisible,
  closeOverlay,
  isDetailLoading,
  detailApiError,
  detailedObservationData, // Add observationNotes to the component props
}) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  // Get observation questions from Redux state
  const observationQuestions = useSelector(
    (state: any) => state.observation.observationQuestions
  );

  // Helper function to get question text by ID
  const getQuestionText = (questionId: number) => {
    if (!observationQuestions || !Array.isArray(observationQuestions)) {
      return `Question ID: ${questionId}`;
    }
    const match = observationQuestions.find(
      (q: any) => q.id === questionId
    );
    return match
      ? match.questionText || `Question ID: ${questionId}`
      : `Question ID: ${questionId}`;
  };

  // Helper function to render Base64 images with click to expand
  const renderBase64Image = (
    base64String: string | null,
    alt: string = "Image",
    style: any = {}
  ) => {
    if (!base64String) {
      return null;
    }
    
    let imageUri: string;
    try {
      // Check if the base64 string already has the data:image prefix
      if (base64String.startsWith("data:image")) {
        imageUri = base64String;
      } else if (base64String.startsWith("https")) {
        // If it's a URL, use it directly
        imageUri = base64String;
      } else {
        // Add the prefix if it's missing
        imageUri = `data:image/jpeg;base64,${base64String}`;
      }

      return (
        <ExpandableImage
          imageUri={imageUri}
          alt={alt}
          style={style}
          containerStyle={{ marginBottom: 8 }}
        />
      );
    } catch (error) {
      return (
        <View style={[styles.errorImageContainer, style]}>
          <Text style={styles.errorImageText}>Unable to display image</Text>
        </View>
      );
    }
  };
  // Prepare miscellaneous images data
  const miscImages = [];
  if (detailedObservationData?.Miscell_Image) {
    miscImages.push({
      imageUrl: detailedObservationData.Miscell_Image,
      description: detailedObservationData.Miscell_Image_Desc || "",
    });
  }
  if (detailedObservationData?.Miscell_Image2) {
    miscImages.push({
      imageUrl: detailedObservationData.Miscell_Image2,
      description: detailedObservationData.Miscell_Image2_Desc || "",
    });
  }

  // Determine observation type from selectedRowData
  const observationType = selectedRowData?.type?.toLowerCase() || "";
  const isPulseCheck = observationType.includes("pulse");

  return (
    <>
      {selectedRowData && (
        <LHOverlay
          visible={isOverlayVisible}
          onDismiss={closeOverlay}
          title="Observation Details"
          size="large"
        >
          <View style={styles.cardContainer}>
            {/* Display loading state while fetching details */}
            {isDetailLoading && (
              <View style={styles.messageContainer}>
                <Text style={styles.loadingText}>
                  Loading observation details...
                </Text>
              </View>
            )}

            {/* Display error if API call fails */}
            {detailApiError && (
              <View style={styles.messageContainer}>
                <Text style={styles.errorText}>Error: {detailApiError}</Text>
              </View>
            )}

            {/* Display detailed data from API - only specific fields with values */}
            {detailedObservationData && (
              <View style={styles.cardGrid}>
                {/* Observations Card - showing selected questions */}
                {detailedObservationData.selectedItems &&
                  Array.isArray(detailedObservationData.selectedItems) &&
                  detailedObservationData.selectedItems.length > 0 && (
                    <View style={styles.fullWidthCard}>
                      <LhCardDisplay
                        headerTitle="Observations"
                        displayValue={
                          detailedObservationData.selectedItems
                            .map((item: any) => getQuestionText(item.observationId))
                            .join(", ") || "No observations recorded."
                        }
                        width="100%"
                      />
                    </View>
                  )}
                {/* Table for all non-null fields */}
                <View style={[styles.cardGridItem, styles.fullWidthCard]}>
                  <View style={styles.card}>
                    <View style={styles.lhCardHeader}>
                      <Text style={styles.lhCardHeaderText}>
                        Observation Details
                      </Text>
                    </View>
                    <View style={styles.tableContainer}>
                      {(() => {
                        const fields = [
                          "Observation_Notes",
                          "Metrics_Discussed",
                          "Last_Shift_Results",
                          "Target_Results",
                          "Actual_Results",
                          // Only include 'Met_Goal' if isPulseCheck is true
                          ...(isPulseCheck ? ["Met_Goal"] : []),
                          "First_Check_Smart_Objective",
                          "Second_Check_Smart_Objective_Results",
                        ];
                        const nonNullFields = fields.filter((key) => {
                          if (key === "Met_Goal") {
                            // Show Met_Goal if it is not null or undefined (allow boolean or string)
                            return (
                              detailedObservationData[key] !== null &&
                              detailedObservationData[key] !== undefined
                            );
                          }
                          // For other fields, skip null, undefined, or empty string
                          return (
                            detailedObservationData[key] !== null &&
                            detailedObservationData[key] !== undefined &&
                            detailedObservationData[key] !== ""
                          );
                        });
                        if (nonNullFields.length === 0) {
                          return (
                            <Text style={styles.infoText}>
                              No observation details available.
                            </Text>
                          );
                        }
                        return nonNullFields.map((key, index) => {
                          let value = detailedObservationData[key];
                          if (key === "Met_Goal") {
                            if (typeof value === "boolean") {
                              value = value ? "Yes" : "No";
                            }
                          }
                          const formattedKey = key
                            .replace(/_/g, " ")
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (str) => str.toUpperCase());
                          return (
                            <View
                              key={index}
                              style={[
                                styles.tableRow,
                                index === nonNullFields.length - 1
                                  ? null
                                  : styles.borderBottom,
                              ]}
                            >
                              <Text style={styles.tableLabel}>
                                {formattedKey}:
                              </Text>
                              <Text style={styles.tableValue}>
                                {String(value)}
                              </Text>
                            </View>
                          );
                        });
                      })()}
                    </View>
                  </View>
                </View>
                {/* Miscellaneous Observations Card */}
                <View style={[styles.cardGridItem, styles.fullWidthCard]}>
                  <View style={styles.card}>
                    {/* Styled header similar to LhCardDisplay */}
                    <View style={styles.lhCardHeader}>
                      <Text style={styles.lhCardHeaderText}>
                        Miscellaneous Observations
                      </Text>
                    </View>
                    {miscImages.length > 0 ? (
                      miscImages.map((img: any, idx: number) => (
                        <View key={idx} style={{ padding: 12 }}>
                          {img.description ? (
                            <Text style={styles.imageDescriptionText}>
                              {img.description}
                            </Text>
                          ) : null}
                          {img.imageUrl
                            ? renderBase64Image(
                                img.imageUrl,
                                `Miscellaneous Image ${idx + 1}`,
                                { height: 120, marginTop: 4, width: "25%" }
                              )
                            : null}
                        </View>
                      ))
                    ) : (
                      <>
                        {detailedObservationData?.Miscell_Image_Desc && (
                          <View style={{ padding: 12 }}>
                            <Text style={styles.imageDescriptionText}>
                              {detailedObservationData.Miscell_Image_Desc}
                            </Text>
                          </View>
                        )}
                        {detailedObservationData?.Miscell_Image2_Desc && (
                          <View style={{ padding: 12 }}>
                            <Text style={styles.imageDescriptionText}>
                              {detailedObservationData.Miscell_Image2_Desc}
                            </Text>
                          </View>
                        )}
                        {!detailedObservationData?.Miscell_Image_Desc &&
                          !detailedObservationData?.Miscell_Image2_Desc && (
                            <Text style={styles.infoText}>
                              No miscellaneous observations recorded.
                            </Text>
                          )}
                      </>
                    )}
                  </View>
                </View>
              </View>
            )}
          </View>
        </LHOverlay>
      )}
    </>
  );
};

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    cardContainer: {
      padding: 16,
      backgroundColor: colors.background.surface,
    },
    cardGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "space-between",
    },
    cardGridItem: {
      width: Platform.OS === "web" ? "48%" : "100%",
      marginBottom: 16,
    },
    fullWidthCard: {
      width: "100%",
      marginBottom: 16,
    },
    tableContainer: {
      padding: 12,
      backgroundColor: colors.background.surface,
    },
    tableRow: {
      flexDirection: "row",
      paddingVertical: 10,
      paddingHorizontal: 12,
    },
    borderBottom: {
      borderBottomWidth: 1,
      borderBottomColor: colors.border.default,
    },
    tableLabel: {
      flex: 1,
      fontWeight: "600",
      color: colors.text.primary,
      paddingRight: 8,
    },
    tableValue: {
      flex: 2,
      color: colors.text.primary,
    },
    messageContainer: {
      padding: 16,
      alignItems: "center",
    },
    loadingText: {
      fontSize: 16,
      color: colors.brand.primary,
    },
    errorText: {
      fontSize: 16,
      color: colors.status.error,
    },
    infoText: {
      fontSize: 16,
      color: colors.text.primary,
      padding: 12,
    },
    card: {
      borderRadius: 8,
      shadowColor: "#000",
      shadowOpacity: 0.05,
      shadowRadius: 4,
      elevation: 2,
      overflow: "hidden",
      backgroundColor: colors.background.surface,
      borderWidth: 1,
      borderColor: colors.border.default,
    },
    lhCardHeader: {
      backgroundColor: "rgb(26, 115, 232)",
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
      paddingVertical: 8,
      paddingHorizontal: 16,
      borderBottomWidth: 2,
      borderBottomColor: "#005BB5",
      marginBottom: 12,
    },
    lhCardHeaderText: {
      color: "#fff",
      fontWeight: "bold",
      fontSize: 18,
      letterSpacing: 0.5,
    },
    imagePreview: {
      width: "100%",
      height: 200,
      borderRadius: 4,
      marginBottom: 8,
    },
    errorImageContainer: {
      width: "100%",
      height: 150,
      backgroundColor: colors.statusBackground.error,
      justifyContent: "center",
      alignItems: "center",
      borderRadius: 4,
      marginBottom: 8,
    },
    errorImageText: {
      color: colors.status.error,
      fontWeight: "bold",
    },
    imageDescriptionText: {
      marginBottom: 8,
      color: colors.text.primary,
    },
    detailSectionTitle: {
      fontSize: 20,
      fontWeight: "bold",
      marginTop: 24,
      marginBottom: 16,
      color: colors.brand.primary,
      borderBottomWidth: 1,
      borderBottomColor: colors.border.default,
      paddingBottom: 8,
    },
    questionItem: {
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border.default,
    },
  });

export default ObservationDetails;
